// Catch-up fetch run after a websocket reconnect. While the socket was down no
// *_NEW / *_UPDATE / TRIGGER_RELOAD_* events arrived, so any list that is only
// kept fresh via socket pushes — most notably pfFiles, which is loaded once at
// project entry and never re-fetched on tab navigation — is stale until the
// user re-enters the project. Re-pull every project-scoped list the socket
// would otherwise update.
//
// Each store tracks the project id it was last fetched for via
// `state.<store>.loadedProjectId`. We only refetch stores whose
// `loadedProjectId` matches the currently loaded project — stores the user
// has never opened (e.g. plans/photos when the user only viewed tasks) stay
// untouched until their tab is entered, where the existing route guard
// performs the initial fetch.
const REFETCHERS = [
  {
    store: 'pfFiles', dispatch: 'pfFiles/fetch', buildPayload: projectId => projectId,
  },
  {
    store: 'tasks', dispatch: 'tasks/fetch', buildPayload: projectId => ({ projectId }),
  },
  {
    store: 'documents', dispatch: 'documents/fetch', buildPayload: projectId => projectId,
  },
  {
    store: 'plans', dispatch: 'plans/fetch', buildPayload: projectId => projectId,
  },
  {
    store: 'photos', dispatch: 'photos/fetch', buildPayload: projectId => ({ projectId }),
  },
  {
    store: 'locationLayers', dispatch: 'locationLayers/fetch', buildPayload: projectId => ({ projectId }),
  },
  {
    store: 'activities', dispatch: 'activities/queryActivities', buildPayload: projectId => ({ projectId }),
  },
]

export default async function refetchProjectData ({ store, route }) {
  const projectId = store.state?.project?.project?._id

  if (!projectId) {
    return
  }

  // project/fetch is always refreshed: it covers participants, filters and
  // the page header, all of which are also pushed via socket and would
  // otherwise stay stale. It's also the gate that re-validates project access
  // (e.g. permission revoked while disconnected).
  const promises = [ store.dispatch('project/fetch', projectId) ]

  for (const { store: storeName, dispatch, buildPayload } of REFETCHERS) {
    if (store.state[storeName]?.loadedProjectId === projectId) {
      promises.push(store.dispatch(dispatch, buildPayload(projectId)))
    }
  }

  // Print jobs aren't a project-scoped list (no loadedProjectId gate): recover
  // any in-flight async PDF render whose PRINT_JOB_UPDATE was missed during the
  // outage. No-op when there are no active jobs.
  promises.push(store.dispatch('printJobs/refetchActive'))

  // Dashboard counters are debounced off socket events, so without a refetch
  // they never catch up after a reconnect with no follow-up events. Only
  // relevant when the user is actually on the dashboard.
  if (route?.name === 'projects-projectId' && store.state.project.dashboardCounts) {
    promises.push(store.dispatch('project/fetchDashboardCounts', projectId))
  }

  await Promise.all(promises)
}
